import React, { useEffect, useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { dashboardUpdateBusinessNotesRequest } from '@/modules/dashboard/dashboardActions';
import { sendAnalytics } from '@/services/analyticsService';
import Dropdown from '@/components/Dropdown/Dropdown';
import './listItemRedesign.scss';
import Moment from 'react-moment';
import 'moment-timezone';
import {
	EnvelopeCommunications,
	EnvelopeOpened,
	Checkmark,
	MessengerUnfilled,
	PhoneCommunications,
	Instagram,
	CheckmarkIcon
} from '@/assets/images';
import { debounce } from 'lodash';

interface BusinessData {
	businessId?: string;
	_id?: string;
	emailIsRead?: boolean;
	[key: string]: any; // To allow other properties not explicitly defined
}

type ContactInfo = {
	type: 'email' | 'phone';
	data: string;
	checked: boolean;
};

type ContactData = {
	[key: string]: {
		date: string;
	};
};

interface ListItemRedesignProps {
	newMessage: boolean;
	selected: boolean;
	businessData: BusinessData;
	toggleBusinessPreview: (
		businessId: string,
		closeOnly?: boolean,
		openToEmail?: boolean
	) => void;
	initialHeaders: string[];
	pageName: string;
	checkboxList: { [key: string]: boolean };
	clickItemCheckbox: (id: string) => void;
	isClicked: boolean;
}

const ListItemRedesign: React.FC<ListItemRedesignProps> = ({
	newMessage,
	selected,
	businessData,
	toggleBusinessPreview,
	initialHeaders,
	pageName,
	checkboxList,
	clickItemCheckbox
}) => {
	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);
	const query = useSelector((state: RootState) =>
		state.dashboard.get('query').toJS()
	);

	const dispatch = useDispatch();

	const [headers, setHeaders] = useState(initialHeaders); // Set initial headers
	const [isContactInfo, setIsContactInfo] = useState(false);

	const setBusinessNotes = (businessId: string, notes: UserNotes) =>
		dispatch(dashboardUpdateBusinessNotesRequest({ businessId, notes }));

	const dropdownColor = () => {
		if (selected) return 'dark';
		if (newMessage) return 'light';
		return 'medium';
	};

	const getMostRecentContact = (contactData: ContactData) => {
		let latest = 0;
		Object.keys(contactData).forEach(key => {
			const date = new Date(contactData[key].date).getTime();
			if (date > latest) latest = date;
		});
		return latest;
	};

	const dropdownTitle = () => {
		if (businessData?.lead === true) {
			return 'Lead';
		}

		if (JSON.stringify(businessData?.lead) === 'false') {
			return 'No Lead';
		}

		return 'Lead Status';
	};
	const getBusinessId = (businessData: BusinessData) =>
		businessData?.businessId || businessData?._id;

	const setLead = leadStatus => {
		let lead;
		const businessId = getBusinessId(businessData);

		if (leadStatus === 'Lead') {
			lead = true;
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `Marked a business 'Lead'`,
					properties: {
						source: pageName,
						business: businessData?.name,
						businessId
					}
				});
			}
		} else if (leadStatus === 'No Lead') {
			lead = false;
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `Marked a business 'No Lead'`,
					properties: {
						source: pageName,
						business: businessData?.name,
						businessId
					}
				});
			}
		} else if (leadStatus === 'Lead Status') {
			lead = undefined;
			if (user && user.email) {
				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: `Remove lead status from a business`,
					properties: {
						source: pageName,
						business: businessData?.name,
						businessId
					}
				});
			}
		}
		const notes = {
			lead,
			isContacted: businessData.isContacted,
			note: businessData.note,
			contactType: businessData.contactType,
			userAddedData: businessData.userAddedData
		};

		setBusinessNotes(businessId, notes);
	};

	// This copy can only be used with the semantics of `emailStatusCount` returned by the Communication Center API call
	//  which is /api/note/getContactedBusiness, where the value returns the sum of all email opens
	//  This function is used to display the hovertext for the email icon
	// If other pages leverage BusinessList and start using the `Contact Method` header, the semantics of the envelope hover
	// may need to be adjusted or the backend query.
	const displayEmailHovertext = () => {
		if (businessData.emailStatusCount > 9) {
			return 'This business has been contacted by email, and the all the emails were opened more than 9 times.';
		}

		if (businessData.emailStatusCount > 1) {
			return `This business has been contacted by email, and the all the emails were opened ${businessData.emailStatusCount} times.`;
		}

		if (businessData.emailStatusCount === 1) {
			return 'This business has been contacted by email, and the emails were opened once.';
		}

		return 'This business has been contacted by email.';
	};

	const renderSanitizedMessagePreview = (message: string) => {
		const sanitizedMessage = DOMPurify.sanitize(message)
			.replace(/<\/?p[^>]*>/g, '')
			.replace(/<br\s*\/?>/gi, ' ');
		return sanitizedMessage;
	};

	const clickCheckbox = (evt: React.MouseEvent) => {
		evt.stopPropagation();

		const businessId = businessData.businessId || businessData._id;

		// Check current state in checkboxList
		const isCurrentlyActivated = checkboxList[businessId];

		// Update the DOM class explicitly
		if (isCurrentlyActivated) {
			evt.currentTarget.classList.remove('activated');
		} else {
			evt.currentTarget.classList.add('activated');
		}

		// Propagate the click action to update state in the parent
		clickItemCheckbox(businessId);
	};

	const renderColumnContent = (header: string) => {
		switch (header) {
			case 'Checkboxes':
				return (
					<div
						className={`list-item-checkbox-list-item-inner list-item-checkbox-list-item-${pageName}-inner ${
							checkboxList[
								businessData.businessId || businessData._id
							]
								? 'activated'
								: ''
						}`}
						data-business-id={
							businessData.businessId || businessData._id
						} // Unique identifier for each checkbox
						onClick={clickCheckbox}
					>
						<div
							className={`list-item-checkbox-list-item-${pageName}-inner-check-left`}
						/>
						<div
							className={`list-item-checkbox-list-item-${pageName}-inner-check-right`}
						/>
					</div>
				);
			case 'Name':
			case 'Business Name': {
				const nameRef = useRef<HTMLSpanElement | null>(null);
				const [isTruncated, setIsTruncated] = useState(false);

				useEffect(() => {
					let isMounted = true;
					const element = nameRef.current;

					const checkTruncation = debounce(() => {
						if (isMounted && element) {
							const isOverflowing =
								element.scrollHeight > element.clientHeight;
							setIsTruncated(isOverflowing);
						}
					}, 100);

					const resizeObserver = new ResizeObserver(checkTruncation);

					if (element) {
						resizeObserver.observe(element);
						checkTruncation(); // Initial check
					}

					return () => {
						isMounted = false; // Avoid updates after unmounting
						resizeObserver.disconnect();
						checkTruncation.cancel();
					};
				}, [businessData?.businessName, businessData?.name]);

				return (
					<span
						ref={nameRef}
						className={`${pageName}-item-name ${
							isTruncated ? 'truncated-hover' : ''
						}`}
						title={
							isTruncated
								? businessData?.businessName ||
									businessData?.name
								: undefined
						}
					>
						<span
							className={
								businessData?.emailIsRead === false
									? `${pageName}-list-item-name-unread`
									: ''
							}
						>
							{businessData?.businessName || businessData?.name}
						</span>
						{businessData?.emailIsRead === false && (
							<span
								className={`${pageName}-list-item-name-dot`}
							/>
						)}
					</span>
				);
			}
			case 'Contact Information':
				if (isContactInfo) {
					return null;
				}
				return (
					<>
						<span className={`${pageName}-list-item-email`}>
							{businessData?.emailRepliedSubject ||
								businessData?.emailSubject ||
								'Email'}
						</span>
						{businessData?.emailRepliedBody ||
						businessData?.emailBody ? (
							<span
								className={`${pageName}-list-item-message-preview`}
								dangerouslySetInnerHTML={{
									__html: renderSanitizedMessagePreview(
										businessData?.emailRepliedBody ||
											businessData?.emailBody
									)
								}}
							/>
						) : null}
					</>
				);
			case 'RE2 Score':
				return (
					<span className={`${pageName}-RE2-score`}>
						{Math.floor(businessData?.score || 0)}
					</span>
				);
			case 'Contact Method':
				return (
					<div className="contact-method-icons-group">
						<div className="contact-method-container">
							{/* Email */}
							{(businessData?.contactType?.emailPrimary ||
								businessData?.contactType?.emailSecondary ||
								businessData?.contactType?.thirdEmail ||
								businessData?.userAddedData.filter(
									(item: ContactInfo) =>
										item.type === 'email' &&
										item.checked === true
								).length > 0) && (
								<div
									className={`${pageName}-list-item-icon-email-div`}
								>
									<div
										className={`list-item-icon email ${
											businessData?.contactType
												?.emailPrimary
												? 'checked'
												: ''
										}`}
									>
										{/* Show Envelope based on email status */}
										{parseInt(
											businessData?.emailStatusCount
										) === 0 ||
										!businessData?.emailStatusCount ? (
											<EnvelopeCommunications
												className="list-item-icon-envelope--closed"
												height={28}
												width={28}
											/>
										) : (
											''
										)}
										{/* Show opened envelope if emailStatusCount > 0 */}
										{typeof businessData?.emailStatusCount !==
											'undefined' &&
											parseInt(
												businessData?.emailStatusCount
											) > 0 && (
												<span className="list-item-opened">
													<EnvelopeOpened
														className="list-item-icon-envelope--opened"
														height={28}
														width={28}
													/>
													<span className="list-item-opened-count">
														{businessData?.emailStatusCount >
														9
															? '9+'
															: businessData?.emailStatusCount}
													</span>
												</span>
											)}

										{/* Checkmark */}
										<Checkmark
											className="list-item-icon-checkmark"
											height={19}
											width={19}
										/>

										{/* Hover text */}
										<span className="hover-text">
											{displayEmailHovertext()}
										</span>
									</div>

									{/* Hovertext for contacted/not contacted */}
								</div>
							)}

							{/* Phone */}
							{businessData?.contactType?.phone && (
								<div className="contact-method-icon-phone-div">
									<div
										className={`list-item-icon phone ${
											businessData?.contactType?.phone
												? 'checked'
												: ''
										}`}
									>
										<PhoneCommunications
											className="list-item-icon-phone"
											height={19}
											width={19}
										/>
										<Checkmark
											className="list-item-icon-checkmark"
											height={19}
											width={19}
										/>
										{/* Hover text */}
										<span className="hover-text">
											This business has been contacted by
											phone.
										</span>
									</div>
								</div>
							)}

							{/* Messenger */}
							{businessData?.contactType?.messenger && (
								<div className="contact-method-icon-messenger-div">
									<div
										className={`list-item-icon messenger ${
											businessData?.contactType?.messenger
												? 'checked'
												: ''
										}`}
									>
										<MessengerUnfilled
											className="list-item-icon-messenger"
											height={28}
											width={28}
										/>
										<Checkmark
											className="list-item-icon-checkmark"
											height={19}
											width={19}
										/>
										<span className="hover-text">
											This business has been contacted by
											Facebook Messenger.
										</span>
									</div>
								</div>
							)}

							{/* Instagram */}
							{businessData?.contactType?.instagramMessenger && (
								<div className="contact-method-icon-instagram-div">
									<div
										className={`list-item-icon instagram ${
											businessData?.contactType
												?.instagramMessenger
												? 'checked'
												: ''
										}`}
									>
										<Instagram
											className="list-item-icon-instagram"
											height={19}
											width={19}
										/>
										<Checkmark
											className="list-item-icon-checkmark"
											height={19}
											width={19}
										/>
										<span className="hover-text">
											This business has been contacted by
											Instagram Messenger.
										</span>
									</div>
								</div>
							)}
						</div>
					</div>
				);

			case 'Last Contacted':
				return businessData?.lastContact ? (
					<Moment format="MMM Do">
						{getMostRecentContact(businessData?.lastContact)}
					</Moment>
				) : null;

			case 'Locations':
				const locationCount = businessData?.locations;

				const displayLocationCount = () => {
					if (locationCount === 1) return '1';
					if (locationCount > 1 && locationCount <= 10) return '2-10';
					if (locationCount > 10) return '10+';
					return '-';
				};

				return <div>{displayLocationCount()}</div>;

			case 'Categories': {
				const categoryRef = useRef<HTMLDivElement | null>(null);
				const [isTruncated, setIsTruncated] = useState(false);

				useEffect(() => {
					const element = categoryRef.current;

					const checkTruncation = () => {
						if (element) {
							const isOverflowing =
								element.scrollHeight > element.clientHeight;
							setIsTruncated(isOverflowing);
						}
					};

					// Observe size changes
					const resizeObserver = new ResizeObserver(checkTruncation);

					if (element) {
						resizeObserver.observe(element);
						checkTruncation(); // Initial check
					}

					return () => {
						resizeObserver.disconnect(); // Cleanup
					};
				}, [businessData?.categories?.display]);

				return businessData?.categories?.display ? (
					<div
						className={`${pageName}-list-item-column-categories`}
						title={
							isTruncated
								? businessData.categories.display
								: undefined
						} // Show hover only if truncated
					>
						<div
							ref={categoryRef}
							className={`${pageName}-categories-text ${
								isTruncated ? 'truncated-hover' : ''
							}`}
						>
							{businessData.categories.display
								.split('\n') // Split into array
								.map(
									(
										category: string,
										index: number,
										arr: string[]
									) => (
										<span key={index}>
											{category}
											{index < arr.length - 1 ? ', ' : ''}
										</span>
									)
								)}
						</div>
					</div>
				) : (
					<span>No categories</span>
				);
			}
			case 'Contacted':
				const isContacted = businessData?.isContacted;

				return (
					<span className="contacted-wrapper">
						{isContacted && (
							<>
								<CheckmarkIcon
									className="contacted-checkmark"
									width={24}
									height={24}
								/>
								<span className="contacted-hovertext">
									Edit a business's contacted status by
									clicking into the business
								</span>
							</>
						)}
					</span>
				);

			case 'Leads':
			case 'Lead Status':
				return (
					<Dropdown
						title={dropdownTitle()}
						headerColor={dropdownColor()}
						dropdownColor={dropdownColor()}
						setLead={setLead}
						list={[
							{
								id: 1,
								title: 'Lead',
								borderColor: 'green'
							},
							{
								id: 2,
								title: 'No Lead',
								borderColor: 'red'
							},
							{
								id: 3,
								title: 'Lead Status',
								borderColor: undefined
							}
						]}
					/>
				);
			default:
				return null;
		}
	};

	// Determine the number of visible columns
	const headerClassMap: { [key: string]: string } = {
		Checkboxes: 'checkboxes',
		'Business Name': 'business-name',
		'Contact Information': 'contact-information',
		'Contact Method': 'contact-method',
		'Last Contacted': 'last-contacted',
		'Lead Status': 'lead-status',
		'RE2 Score': 're2-score',
		Categories: 'categories',
		Name: 'name',
		Locations: 'locations',
		Contacted: 'contacted',
		Leads: 'leads'
	};

	return (
		<div>
			<ul
				className={`${pageName}-list-item clickable-list-item ${newMessage ? 'new' : ''} ${selected ? 'clicked' : ''}`}
				onClick={e => {
					const targetClasses = e.target.classList;
					// Check if the clicked element contains any of the classes that should prevent the toggle
					if (
						!targetClasses.contains('dropdown-header') &&
						!targetClasses.contains('dropdown-header-text') &&
						!targetClasses.contains('dropdown-icon')
					) {
						toggleBusinessPreview(
							(businessData.businessId || businessData._id)!
						);
					}
				}}
			>
				<div className={`${pageName}-list-item-content`}>
					{headers.map(header => {
						const columnClassName =
							headerClassMap[header] || `hidden-column`;
						return (
							<div
								key={header || 'empty'} // Ensure a unique key for non-empty headers
								className={`${pageName}-list-item-column-${columnClassName}`}
							>
								{header ? renderColumnContent(header) : null}
							</div>
						);
					})}
				</div>
			</ul>
		</div>
	);
};

export default ListItemRedesign;
